import * as yup from 'yup';
import { getRequest, postRequest, putRequest } from '../../../services/httpService';
import { DropdownOption } from '../../Medical_History_Forms/MenstrualHistory/MenstrualHistory.helpers';

export type BlankEnum = '';
export type NullEnum = 'NULL';

// Interface for Oocyte Retrieval
export interface OocyteRetrievalType {
  cycle: number | null;
  id: number | null;

  cycle_start_date: string | null;
  opu_date: string | null;
  start_time: string;
  end_time: string;

  clinician: number | null;
  embryologist: number | null;
  witness_embryologist: number | null;
  anesthetist: number | null;

  type_of_anesthesia: string;
  no_of_needles_used: number;
  nurse: number | null;
  tube_id: string;

  type_of_needle: string;
  sub_type_of_needle: string;
  difficulty_level: string;
  procedure_findings: string;
  optimum_follicles: OptimumFollicles;
  optimum_follicles_left: number | null;
  optimum_follicles_right: number | null;
  oocytes_retrieved_left: number | null;
  oocytes_retrieved_right: number | null;

  notes: string;
  clinicians_notes: string;
  pre_anesthesia_checkup: boolean | null;
  created_on: string | null;
  clinician_name?: string | null;
  embryologist_name?: string | null;
  witness_embryologist_name?: string | null;
  anesthetist_name?: string | null;
  nurse_name?: string | null;
  type_of_needle_label?: string | null;
  sub_type_of_needle_label?: string | null;
  difficulty_level_label?: string | null;
  type_of_anesthesia_label?: string | null;

}
export interface Permission {
  access: 'add' | 'edit' | 'view' | 'print';
  male: boolean;
  female: boolean;
}


// Initial state for Oocyte Retrieval with default values
export const initialOocyteRetrieval: OocyteRetrievalType = {
  cycle: null,
  id: null,

  cycle_start_date: null,
  opu_date: '',
  start_time: '',
  end_time: '',

  clinician: null,
  embryologist: null,
  witness_embryologist: null,
  anesthetist: null,

  type_of_anesthesia: '',
  no_of_needles_used: 0,
  nurse: null,
  tube_id: '',

  type_of_needle: '',
  sub_type_of_needle: '',
  difficulty_level: '',
  procedure_findings: '',

  optimum_follicles: {
    left_follicles: 0,
    right_follicles: 0,
  },
  optimum_follicles_left: null,
  optimum_follicles_right: null,
  oocytes_retrieved_left: null,
  oocytes_retrieved_right: null,

  notes: '',
  clinicians_notes: '',
  pre_anesthesia_checkup: false,
  created_on :''
};

// Validation schema for Oocyte Retrieval form using Yup
export const OocyteRetrievalValidationSchema = (tGeneral: (key: string) => string) => yup.object({
  cycle: yup
    .number()
    .required(tGeneral('cycleNumberRequired')),

  cycle_start_date: yup
    .string()
    .nullable()
    .required('Cycle Start Date is required'),

  opu_date: yup
    .string()
    .nullable()
    .required('OPU Date is required'),

  start_time: yup
    .string()
    .nullable()
    .required('Start Time is required'),


  end_time: yup
    .string()
    .required('End Time is required')
    .test(
      'is-after-start-time',
      tGeneral('endTimeAfterStartTime'),
      function (value) {
        const { start_time } = this.parent;
        if (!start_time || !value) return true;
        return value > start_time;
      }
    ),

  clinician: yup
    .number()
    .required('Clinician name is required'),

  embryologist: yup
    .number()
    .required('Embryologist name is required'),

  witness_embryologist: yup
    .number()
    .required('Witness Embryologist name is required')
    .test('not-same', tGeneral('witnessEmbryologistError') , function (value) {
      const { embryologist } = this.parent; // Accessing the sibling field
      return value !== embryologist; // Validate that they are not the same
    }),

  anesthetist: yup
    .number()
    .required('Anesthetist name is required'),

  type_of_anesthesia: yup
    .string()
    .nullable()
    .required('Anesthetist name is required'),

  no_of_needles_used: yup
    .number()
    .nullable()
    .required('Number of needles is required'),

  nurse: yup
    .number()
    .nullable(),

  tube_id: yup
    .string()
    .nullable(),

  type_of_needle: yup
    .string()
    .nullable(),

  sub_type_of_needle: yup
    .string()
    .nullable(),

  difficulty_level: yup
    .string()
    .nullable(),

  procedure_findings: yup
    .string()
    .nullable()
    .max(2000, tGeneral('procedureFindingsMaxLength')),

  optimum_follicles_left: yup
    .number()
    .nullable(),

  optimum_follicles_right: yup
    .number()
    .nullable(),

  oocytes_retrieved_left: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError(tGeneral('oocyteRetrievedMustBeNumber'))
    .nullable()
    .min(0, tGeneral('oocyteRetrievedMinZero'))
    .test(
      'left-or-right-required',
      tGeneral('eitherLeftOrRightOocytesRequired'),
      function (value) {
        const { oocytes_retrieved_right } = this.parent as { oocytes_retrieved_right: unknown };

        const leftVal = typeof value === 'number' ? value : null;
        const rightVal = typeof oocytes_retrieved_right === 'number' ? oocytes_retrieved_right as number : null;

        const leftValid = leftVal !== null && !Number.isNaN(leftVal) && leftVal >= 1;
        const rightValid = rightVal !== null && !Number.isNaN(rightVal) && rightVal >= 1;

        // If left has a value but it's < 1, fail with a specific message
        if (leftVal !== null && !Number.isNaN(leftVal) && leftVal < 1) {
          return this.createError({ message: tGeneral('oocyteRetrievedMustBeAtLeast1') });
        }

        // Valid if either side has >=1
        return leftValid || rightValid;
      }
    ),

  oocytes_retrieved_right: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError(tGeneral('oocyteRetrievedMustBeNumber'))
    .nullable()
    .min(0, tGeneral('oocyteRetrievedMinZero'))
    .test(
      'right-or-left-required',
      tGeneral('eitherLeftOrRightOocytesRequired'),
      function (value) {
        const { oocytes_retrieved_left } = this.parent as { oocytes_retrieved_left: unknown };

        const rightVal = typeof value === 'number' ? value : null;
        const leftVal = typeof oocytes_retrieved_left === 'number' ? oocytes_retrieved_left as number : null;

        const rightValid = rightVal !== null && !Number.isNaN(rightVal) && rightVal >= 1;
        const leftValid = leftVal !== null && !Number.isNaN(leftVal) && leftVal >= 1;

        if (rightVal !== null && !Number.isNaN(rightVal) && rightVal < 1) {
          return this.createError({ message: tGeneral('oocyteRetrievedMustBeAtLeast1') });
        }

        return rightValid || leftValid;
      }
    ),

  notes: yup
    .string()
    .nullable()
    .max(2000, tGeneral('notesMaxLength')),

  clinicians_notes: yup
    .string()
    .nullable(),

  pre_anesthesia_checkup: yup
    .boolean()
    .nullable(),
});


// Function to generate the URL for adding oocyte retrieval
export function generateOocyteRetrievalURL(id: number): string {
  return `opu/cycle/${id}`;
}

// Function to add oocyte retrieval 
export function addOocyteRetrieval(data: OocyteRetrievalType): Promise<OocyteRetrievalType> {
  const url = 'opu/';
  return postRequest(url, data, { responseType: 'json' });
}


// Function to get oocyte retrieval
export function getOocyteRetrieval(id: number): Promise<OocyteRetrievalType> {
  return getRequest(generateOocyteRetrievalURL(id), { responseType: 'json' }) as Promise<OocyteRetrievalType>;
}

// Function to update oocyte retrieval
export function updateOocyteRetrieval(id: number, data: OocyteRetrievalType): Promise<OocyteRetrievalType> {
  const url = `opu/${id}`;

  return putRequest(url, data, { responseType: 'json' });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getARTCycleList(id: number): Promise<any> {
  return getRequest(`cycle/patient/${id}`, { responseType: 'json' });
}

// Interfaces representing donor/recipient patient information for oocyte retrieval procedures
export interface Recipient {
  type: string;
  photo: string;
}

export interface SpermSource {
  id: number;
  age: number;
  type: string;
  photo: string;
  gender: string;
  last_name: string;
  created_on: string;
  first_name: string;
  updated_on: string;
  gender_label: string;
  patient_type: string;
  date_of_birth: string;

}
export interface OocyteSource {
  id: number;
  age: number;
  type: string;
  photo: string;
  gender: string;
  last_name: string;
  created_on: string;
  first_name: string;
  updated_on: string;
  gender_label: string;
  patient_type: string;
  date_of_birth: string;
  reason_for_visit: string;
  medical_record_no: string;
  reason_for_visit_label: string;
}

export interface DonorInfo {
  recipient: Recipient;
  sperm_source: SpermSource[];
  oocyte_source: OocyteSource[];
}
export interface Procedure {
  id: number;
  services: number[];
  procedures: string;
  procedures_type: string;
  procedure_notes: string | null;
  created_on: string;
  donor_info: DonorInfo;
  indication: string;
}

export interface EtEvent {
  id: number;
  event_type: string;
  date: string; // ISO date string, e.g., '2025-04-25'
  appointment: appointment;
}

export interface appointment {
  id: number;
  user: number;
  appointment_date: string; // ISO date string
  start_datetime: string; // ISO datetime string
  end_datetime: string; // ISO datetime string
  appointment_reason: string | null;
  patient: number;
  partner: number;
  room: number;
  table: number;
  remark: string;
  status: 'SCHEDULED' | 'CANCELLED' | 'COMPLETED' | string; // Extend as per all statuses used
  type: 'APPOINTMENT' | string; // Extend as needed
  department: number;
  embryologist: number;
}

export interface CycleData {
  id: number;
  procedure: Procedure;
  cycle_start_date: string;
  medication_protocol: number;
  lmp_date: string;
  status: string;
  cycle_number: string;
  oocyte_details?: number | null;
  semen_processing: number;
  semen_freezing?: number;
  iui?: number;
  procedure_list?: Procedure;
  opu_event?: OpuEvent;
  opu_status?: boolean;
  et_event:EtEvent;
  is_surrogate?: boolean;
  is_opu_cycle?: boolean;
}

export interface CycleItem {
  id: number;
  value: string;
  label: string;
  status: string;
  procedure?: string | undefined;
}

// Define a constant for roles to avoid magic strings
export const ROLES = ['DOCTOR', 'ANESTHETIST', 'EMBRYOLOGIST', 'NURSE'] as const;

export interface Clinician {
  id: number;
  first_name: string;
  last_name: string;
  // Add other relevant fields as per your API response
}

// Create a type that represents the role strings
export type Role = typeof ROLES[number]; // 'DOCTOR' | 'ANESTHETIST' | 'EMBRYOLOGIST' | 'NURSE'

interface OptimumFollicles {
  left_follicles: number;
  right_follicles: number;
}

export interface OocyteRetrievalData {
  id?: number;
  cycle?: number;
  opu_date?: string; // Format: YYYY-MM-DD
  start_time: string; // Format: HH:mm:ss
  end_time: string; // Format: HH:mm:ss
  clinician: number;
  embryologist: number;
  witness_embryologist: number;
  anesthetist: number;
  nurse: number;
  type_of_anesthesia: string; // Enum or specific type (e.g., "REGIONAL_ANESTHESIA")
  type_of_needle: string; // Enum or specific type (e.g., "COOK")
  sub_type_of_needle: string; // Enum or specific type (e.g., "SINGLE_LUMEN")
  difficulty_level: string; // Enum or specific type (e.g., "MODERATE")
  procedure_findings: string;
  notes: string;
  optimum_follicles: OptimumFollicles;
  oocytes_retrieved_left: number;
  oocytes_retrieved_right: number;
  tube_id: string;
  no_of_needles_used: number;
  pre_anesthesia_checkup: boolean;
  nurse_name: string;
  anesthetist_name: string;
  witness_embryologist_name: string;
  embryologist_name: string;
  clinician_name: string;
  cycle_start_date: string; // Format: YYYY-MM-DD
  created_on?: string
}


// Define default values for OocyteRetrievalData
export const defaultRetrievalData: OocyteRetrievalData = {
  // id: 0,
  // cycle: 0,
  opu_date: '',
  start_time: '',
  end_time: '',
  clinician: 0,
  embryologist: 0,
  witness_embryologist: 0,
  anesthetist: 0,
  nurse: 0,
  type_of_anesthesia: '',
  type_of_needle: '',
  sub_type_of_needle: '',
  difficulty_level: '',
  procedure_findings: '',
  notes: '',
  optimum_follicles: {
    left_follicles: 0,
    right_follicles: 0,
  },
  oocytes_retrieved_left: 0,
  oocytes_retrieved_right: 0,
  tube_id: '',
  no_of_needles_used: 0,
  pre_anesthesia_checkup: false,
  nurse_name: '',
  anesthetist_name: '',
  witness_embryologist_name: '',
  embryologist_name: '',
  clinician_name: '',
  cycle_start_date: '',
};

export interface CycleDetailsType {
  id: number;
  cycle_number: string;
  cycle_name: string;
  medication_protocol: number;
  cycle_provider: number;
  provider_name: string;
  cycle_coordinator: number;
  coordinator_name: string;
  cycle_creator: number;
  cycle_start_date: string;
  cycle_end_date: string;
  lmp_date: string;
  cycle_creator_name: string;
  cycle_warning: string;
  remarks: string | null;
  status: string;
  patient: number;
  patient_name: string;
  stimulation_chart: number | null;
  optimum_follicles: OptimumFollicles | null;
  opu_event?: OpuEvent;
}

// master integration interface

export interface MasterconfigrutationType {
  OOCYTE_ANESTHESIA?: string | null,
  NEEDLE?: string | null,
  NEEDLE_SUBTYPE?: string | null,
  DIFFICULTY?: string | null,
  TRANSFER_METHOD?: string | null,
  EMBRYO_ANESTHESIA?: string | null,
  EEMBRYO_NDOMETRIUM_PATTERN?: string | null,
  ULTRASOUND_METHOD?: string | null,
  CATHETER?: string | null,
  LEVEL_OF_DIFFICULTY?: string | null,
  LAPARO_ANESTHESIA?: string | null,
  ADHESION_GRADE?: string | null,
  ENDOMETRIOSIS_GRADE?: string | null,
  UTERUS_STATUS?: string | null,
  UTERUS_SIZE?: string | null,
  UTERUS_MOBILITY?: string | null,
  ASSOCIATED_PROCEDURES?: string | null,
  LIVER_STATUS?: string | null,
  CAVITY?: string | null,
  ENDOMETRIUM_PATTERN?: string | null,
  PROCEDURE_DONE?: string | null

}
export interface MasterconfigrutationDetails {
  type_of_anesthesia: DropdownOption[];
  type_of_needle: DropdownOption[];
  sub_type_of_needle: DropdownOption[];
  difficulty_level: DropdownOption[];
  transfer_method: DropdownOption[];
  embryo_Anesthesia: DropdownOption[];
  endometrium_pattern: DropdownOption[];
  ultrasound_method: DropdownOption[];
  catheter: DropdownOption[];
  embryoDifficulty_level: DropdownOption[];
  Laparo_anesthesia_type: DropdownOption[];
  adhesion_grade: DropdownOption[];
  endometriosis_grade: DropdownOption[];
  uterus_status: DropdownOption[];
  uterus_size: DropdownOption[];
  uterus_mobility: DropdownOption[];
  associated_procedures: DropdownOption[];
  liver_undersurface: DropdownOption[];
  procedure_done: DropdownOption[];
  cavity: DropdownOption[];
  endometrium_patterns: DropdownOption[];

}


export function getcycleListUrl(Id: string): string {
  return `/cycle/${Id}/`;
}


export function getcycleList(Id: string): Promise<CycleDetailsType> {
  return getRequest(getcycleListUrl(Id), { responseType: 'json' });
}

export function fetchMasterData(type: string): Promise<MasterconfigrutationType> {
  const url = `clinics/configuration/?module_type=surgery&type=${type}`;
  return getRequest(url, { responseType: 'json' });
}

export interface OpuEvent {
  id: number;
  event_type: string;
  date: string;
  appointment: Appointment;
}

export interface Appointment {
  id: number;
  user: number;
  appointment_date: string;
  start_datetime: string;
  end_datetime: string;
  appointment_reason: number;
  patient: number;
  partner: number;
  room: number;
  table: number;
  remark: string;
  status: string;
  type: string;
  department: number;
  embryologist: number;
}


export interface ServiceItem {
  id: number;
  name: string;
  code: string;
  description: string;
  service_type: string;
}

export interface SemenProcessingItem {
  id: number;
  patient: number;
  source: number;
}


export interface CycleFullData {
  id: number;
  procedure: Procedure & {
    donor_info: DonorInfo;
    indication: string;
    procedure_overview_links: string;
  };
  medication_protocol: string;
  cycle_start_date: string;
  lmp_date: string;
  status: string;
  cycle_number: string;
  services: ServiceItem[];
  stimulation_chart: number;
  iui: unknown[];
  semen_processing: SemenProcessingItem[];
  oocyte_details?: number | null;
  semen_freezing?: unknown[];
  remarks?: string;
  cryopreserved_count?: number;
  fertilized_count?: number;
  fresh_transfer_count?: number;
  frozen_transfer_count?: number;
  opu_status?: boolean;
  oocyte_count?: number;
  cleaved_count?: number;
  opu_event?: OpuEvent | null;
  et_event?: EtEvent | null;
  cancel_reason?: string;
  termination_reason?: string;
  bhcg_date?: string | null;
  outcome?: string | null;
  total_embryooocyte_count?: number;
}
